import { Button, Text, VSpacer } from '@/components/DesignSystem';
import { ManufacturerAvatar } from "@/components/DesignSystem/Avatar/ManufacturerAvatar";
import { QueryKeys } from '@/constants/QueryKeys';
import { DashboardModal } from '@/pages/CustomerDashboard/DashboardModal';
import { PromotionProductsBottomSheet } from '@/pages/Promotions/PromotionProductsBottomSheet';
import { PromotionApi } from '@/utilities/api/PromotionApi';
import { CircularProgress, Divider, Stack, useTheme } from '@mui/material';
import { SponsorType } from '@shared/enums';
import { getJsDateFromString } from '@shared/utilities';
import { sortBy } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

interface PromotionDetailsModalProps {
  accessToken: string,
  brandColor?: string,
  onClose: () => void,
  promotionId: string,
  retailerName: string,
}

export const PromotionDetailsModal = ({
  accessToken,
  brandColor,
  onClose,
  promotionId,
  retailerName,
}: PromotionDetailsModalProps) => {
  const { t } = useTranslation();
  const { data: promotion, isFetching } = useQuery(
    [QueryKeys.GET_PROMOTION, promotionId],
    () => PromotionApi.get(promotionId, accessToken),
  );
  const productsEarnList = sortBy(promotion?.productsForEarningPoints ?? [], ({ name }) => name);
  const productsRedeemList = (
    sortBy(promotion?.productsForRedeemingPoints ?? [], ({ name }) => name)
  );
  const manufacturer = promotion?.manufacturer;
  const locationNames = (promotion?.locations ?? []).map(({ name }) => name).sort();
  const theme = useTheme();
  const [showEarnProducts, setShowEarnProducts] = useState(false);
  const [showRedeemProducts, setShowRedeemProducts] = useState(false);

  const getProductRedeemText = () => {
    if (!promotion || productsRedeemList.length) {
      return undefined;
    }
    if (promotion.sponsorType === SponsorType.Retailer) {
      return t($ => $.promotions.details.allProducts, { retailerName });
    }
    return t($ => $.promotions.details.allManufacturerProducts, {
      manufacturerName: manufacturer?.name ?? 'manufacturer',
      retailerName,
    });
  };

  return (
    <DashboardModal
      onClose={onClose}
      open
      testID="promotion-details-modal"
      title={t($ => $.promotions.details.title)}
    >
      {isFetching || !promotion ? (
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      ) : (
        <>
          <Stack alignItems="center" bgcolor="#393939" p="8px 6px">
            <Text category="label-medium" sx={{ color: 'white' }}>
              {t(
                $ => $.promotions.card.runs,
                {
                  startDate: getJsDateFromString(promotion.startDate),
                  endDate: getJsDateFromString(promotion.endDate),
                },
              )}
            </Text>
          </Stack>
          {!!manufacturer && promotion?.sponsorType === SponsorType.Manufacturer && (
            <>
              <Stack alignItems="center" p="20px 16px">
                <ManufacturerAvatar manufacturer={manufacturer} size={60} />
                <VSpacer size="2" />
                <Text category="headline-medium" textAlign="center">
                  {manufacturer.name}
                </Text>
                <VSpacer size="2" />
                <Text category="overline" sx={{ color: theme.palette.text.secondary }}>
                  {t($ => $.promotions.details.manufacturerPromotion)}
                </Text>
              </Stack>
              <Divider />
            </>
          )}
          <Stack px="16px">
            <Stack pb="20px" pt="24px">
              <Text category="title-large" fontWeight={500} px="16px" textAlign="center">
                {promotion?.name}
              </Text>
              {promotion?.overridePointsEngine && (
                <>
                  <VSpacer size="4"/>
                  <Stack bgcolor="#f7f7f7" p="8px 12px">
                    <Text category="body-xsmall">
                      {t($ => $.promotions.create.step1.manufacturerOnly)}
                    </Text>
                  </Stack>
                </>
              )}
            </Stack>
            <Divider />
            {!!promotion?.description && (
              <>
                <Stack py="16px">
                  <Text category="title-medium">
                    {t($ => $.promotions.details.promotionInfo)}
                  </Text>
                  <Text category="body-medium" sx={{ color: '#525252' }}>
                    {promotion?.description}
                  </Text>
                </Stack>
                <Divider />
              </>
            )}
            {!promotion?.allLocations && (
              <>
                <Stack py="16px">
                  <Text category="title-medium">
                    {t($ => $.promotions.details.participatingLocations, { retailerName })}
                  </Text>
                  <VSpacer size="3" />
                  <Text category="body-medium" sx={{ color: '#525252' }}>
                    {locationNames.join(', ')}
                  </Text>
                </Stack>
                <Divider />
              </>
            )}
            <VSpacer size="5"/>
            <Button
              onClick={() => setShowEarnProducts(true)}
              sx={{
                bgcolor: brandColor,
                borderRadius: '3px',
                color: 'white',
                '&:hover': { bgcolor: brandColor },
              }}
              testID="product-earn-list-button"
              variant="contained"
            >
              {t($ => $.promotions.details.viewProductEarnList)}
            </Button>
            <VSpacer size="5"/>
            <Button
              onClick={() => setShowRedeemProducts(true)}
              sx={{
                bgcolor: brandColor,
                borderRadius: '3px',
                color: 'white',
                '&:hover': { bgcolor: brandColor },
              }}
              testID="product-redeem-list-button"
              variant="contained"
            >
              {t($ => $.promotions.details.viewProductRedeemList)}
            </Button>
            <VSpacer size="5" />
          </Stack>
        </>
      )}
      {showEarnProducts && (
        <PromotionProductsBottomSheet
          brandColor={brandColor}
          onClose={() => setShowEarnProducts(false)}
          products={productsEarnList}
          variant="products"
        />
      )}
      {showRedeemProducts && (
        <PromotionProductsBottomSheet
          brandColor={brandColor}
          emptyText={getProductRedeemText()}
          onClose={() => setShowRedeemProducts(false)}
          products={productsRedeemList}
          variant="redeemable"
        />
      )}
    </DashboardModal>
  );
};
